<script setup>
/**
 * OCHAT: la lista de reservas con sus filtros.
 *
 * El código va primero en la fila porque es lo que el huésped dice por WhatsApp
 * cuando escribe de vuelta: es la columna por la que el equipo busca.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BookingStatusBadge from '../components/BookingStatusBadge.vue';

const store = useStore();
const bookings = useMapGetter('bookings/getBookings');
const uiFlags = useMapGetter('bookings/getUIFlags');

const status = ref('');
const search = ref('');

const fetch = () => {
  store.dispatch('bookings/get', status.value ? { status: status.value } : {});
};

onMounted(fetch);
watch(status, fetch);

// El filtro de texto es local a propósito: sobre una lista ya cargada es
// instantáneo, y el equipo del hotel busca un código que acaba de recibir por
// WhatsApp. Pedirle al servidor por cada tecla sería peor experiencia.
const visible = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return bookings.value;

  return bookings.value.filter(booking => {
    const guest = booking.guest?.name?.toLowerCase() ?? '';
    const phone = booking.guest?.phone ?? '';
    return (
      booking.code.toLowerCase().includes(term) ||
      guest.includes(term) ||
      phone.includes(term)
    );
  });
});

const nightsOf = booking =>
  booking.rooms?.reduce((total, room) => total + room.nights, 0) ?? 0;

const stayOf = booking => {
  const room = booking.rooms?.[0];
  if (!room) return '—';
  return `${room.checkin} → ${room.checkout}`;
};

const roomTypeOf = booking => booking.rooms?.[0]?.room_type?.name ?? '—';

const money = (amount, currency) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: currency || 'ARS' }).format(
    Number(amount || 0)
  );
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-3">
      <input
        v-model="search"
        type="search"
        class="w-64 px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        :placeholder="$t('BOOKINGS.LIST.SEARCH_PLACEHOLDER')"
      />
      <select
        v-model="status"
        class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
      >
        <option value="">{{ $t('BOOKINGS.LIST.ALL_STATUSES') }}</option>
        <option value="held">{{ $t('BOOKINGS.STATUS.HELD') }}</option>
        <option value="confirmed">{{ $t('BOOKINGS.STATUS.CONFIRMED') }}</option>
        <option value="checked_in">{{ $t('BOOKINGS.STATUS.CHECKED_IN') }}</option>
        <option value="checked_out">{{ $t('BOOKINGS.STATUS.CHECKED_OUT') }}</option>
        <option value="cancelled">{{ $t('BOOKINGS.STATUS.CANCELLED') }}</option>
        <option value="no_show">{{ $t('BOOKINGS.STATUS.NO_SHOW') }}</option>
      </select>
      <span v-if="visible.length" class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.LIST.COUNT', { n: visible.length }) }}
      </span>
    </div>

    <p v-if="uiFlags.isFetching" class="text-sm text-n-slate-11">
      {{ $t('BOOKINGS.LIST.LOADING') }}
    </p>

    <p v-else-if="!visible.length" class="py-8 text-sm text-n-slate-11">
      {{ $t('BOOKINGS.LIST.EMPTY') }}
    </p>

    <div v-else class="overflow-x-auto rounded-lg outline outline-1 outline-n-container">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.CODE') }}
            </th>
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.GUEST') }}
            </th>
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.STAY') }}
            </th>
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.ROOM_TYPE') }}
            </th>
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.TOTAL') }}
            </th>
            <th class="px-4 py-3 font-medium text-start text-n-slate-11">
              {{ $t('BOOKINGS.LIST.TABLE.STATUS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="booking in visible"
            :key="booking.id"
            class="border-b border-n-weak last:border-0"
          >
            <td class="px-4 py-3 font-medium text-n-slate-12">{{ booking.code }}</td>
            <td class="px-4 py-3">
              <div class="text-n-slate-12">{{ booking.guest?.name || '—' }}</div>
              <div class="text-n-slate-11">{{ booking.guest?.phone }}</div>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              <div>{{ stayOf(booking) }}</div>
              <div>{{ $t('BOOKINGS.LIST.NIGHTS', { n: nightsOf(booking) }) }}</div>
            </td>
            <td class="px-4 py-3 text-n-slate-11">{{ roomTypeOf(booking) }}</td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ money(booking.total_amount, booking.currency) }}
            </td>
            <td class="px-4 py-3">
              <BookingStatusBadge :status="booking.status" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
